import React from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  useIntl,
} from 'react-intl';
import {
  Alert,
  Badge,
  DescriptionList,
  Heading,
  LinkAsButton,
  Panel,
  SelectField,
  Spinner,
} from '@printdeal/ui-library';
import { navigate } from 'gatsby';
import {
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params';
import { useLocation } from '@gatsbyjs/reach-router';
import queryString from 'query-string';
import { FeatureToggle } from '@printdeal/feature-management/react';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';
import { formatDateEnriched } from '../../../../../helpers/dateEnriched';
import useGetInvoicesQuery from '../../hooks/invoices/useGetInvoicesQuery';
import InvoiceChangeAscriptionButton from './components/InvoiceChangeAscriptionButton';
import InvoiceCallbackAlerts from './components/InvoiceCallbackAlerts';
import InvoiceDownloadButton from './components/InvoiceDownloadButton';
import { Invoice } from '../../../../../types/invoices/Invoices';
import InvoicePaymentButton from './components/InvoicePaymentButton';

const calculateDateRange = (year: number, month: string) => {
  if (!month) {
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);
    return {
      fromDate: formatDateEnriched(startDate),
      toDate: formatDateEnriched(endDate),
    };
  }
  const startDate = new Date(year, Number(month) - 1, 1);
  const endDate = new Date(year, Number(month), 0);
  return {
    fromDate: formatDateEnriched(startDate),
    toDate: formatDateEnriched(endDate),
  };
};

const InvoicesPage = () => {
  const intl = useIntl();
  const location = useLocation();
  const [paymentStatusFilter, setPaymentStatusFilter] = useQueryParam('paymentState', withDefault(StringParam, 'all'));

  const currentYear = new Date().getFullYear();
  const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const [yearFilter, setYearFilter] = useQueryParam('year', withDefault(StringParam, currentYear.toString()));
  const [monthFilter, setMonthFilter] = useQueryParam('month', withDefault(StringParam, currentMonth));

  const queryParameters = queryString.parse(location.search);
  const getInvoicesQuery = useGetInvoicesQuery({
    dates: calculateDateRange(Number(yearFilter), monthFilter),
    paymentStatusFilter,
  });

  const invoices = getInvoicesQuery.data ?? [];

  const needPayment = (invoice: Invoice) => {
    if (invoice.paymentStatus === 'Paid') {
      return false;
    }

    if (Number(invoice.totalAmount) <= 0) {
      return false;
    }

    if (queryParameters.authResult === 'AUTHORISED' && invoice.invoiceNumber === queryParameters.invoiceNumber) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Head title={intl.formatMessage({ id: 'account.invoices.page_title' })} />
      <div className="pd-flex pd-flex-col pd-gap-4">
        <Breadcrumb>
          <Breadcrumb.Item to="..">
            <FormattedMessage id="account.index.title" defaultMessage="My account" />
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage>
            <FormattedMessage id="account.invoices.page_title" defaultMessage="My invoices" />
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className="pd-flex pd-items-center pd-justify-between">
          <Heading as="h2">
            <FormattedMessage id="account.invoices.page_title" defaultMessage="My invoices" />
          </Heading>
        </div>

        <FeatureToggle id="mmm_change_ascription">
          <FeatureToggle.Disabled>
            <Alert variant="warning">
              <FormattedMessage id="account.invoices.change_ascription_disabled" defaultMessage="The change ascription possibility has been temporary disabled. We will reinstate it as soon as possible. We are sorry for the inconvenience." />
            </Alert>
          </FeatureToggle.Disabled>
        </FeatureToggle>

        <InvoiceCallbackAlerts />

        <div className="pd-grid pd-grid-cols-3 pd-grid-rows-1 pd-gap-4">
          <div className="pd-col-span-1">
            <SelectField
              id="invoice-payment-filter"
              name="invoice-payment-filter"
              label={intl.formatMessage({
                id: 'account.invoices.paymentStatusFilter.label',
                defaultMessage: 'Payment status',
              })}
              onChange={(event) => {
                setPaymentStatusFilter(event.target.value);
              }}
            >
              <SelectField.Option value="all" selected={paymentStatusFilter === 'all'}>
                <FormattedMessage id="account.invoices.paymentStatusFilter.all" defaultMessage="All" />
              </SelectField.Option>
              <SelectField.Option value="unpaid" selected={paymentStatusFilter === 'unpaid'}>
                <FormattedMessage id="account.invoices.paymentStatusFilter.unpaid" defaultMessage="Unpaid" />
              </SelectField.Option>
              <SelectField.Option value="paid" selected={paymentStatusFilter === 'paid'}>
                <FormattedMessage id="account.invoices.paymentStatusFilter.paid" defaultMessage="Paid" />
              </SelectField.Option>
            </SelectField>
          </div>
          <div className="pd-col-span-1">
            <SelectField
              id="invoice-from-year-filter"
              name="invoice-from-year-filter"
              label={intl.formatMessage({
                id: 'account.invoices.yearFromFilter.label',
                defaultMessage: 'Select year',
              })}
              onChange={(event) => {
                setYearFilter(event.target.value);
              }}
            >
              {years.map((year) => (
                <SelectField.Option
                  key={year}
                  value={year.toString()}
                  selected={yearFilter === year.toString()}
                >
                  {year}
                </SelectField.Option>
              ))}
            </SelectField>
          </div>
          <div className="pd-col-span-1">
            <SelectField
              id="invoice-from-month-filter"
              name="invoice-from-month-filter"
              label={intl.formatMessage({
                id: 'account.invoices.monthFromFilter.label',
                defaultMessage: 'Select month',
              })}
              onChange={(event) => {
                setMonthFilter(event.target.value);
              }}
            >
              <SelectField.Option value="" selected={monthFilter === ''}>
                <FormattedMessage id="account.invoices.month.none" defaultMessage="-- all months --" />
              </SelectField.Option>
              {Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0')).map((month) => (
                <SelectField.Option
                  key={month}
                  value={month}
                  selected={monthFilter === month}
                >
                  {intl.formatMessage({
                    id: `account.invoices.month.${month}`,
                    defaultMessage: new Date(0, Number(month) - 1).toLocaleString(intl.locale, { month: 'long' }),
                  })}
                </SelectField.Option>
              ))}
            </SelectField>
          </div>
        </div>

        {getInvoicesQuery.isError && (
          <Alert variant="error">
            <FormattedMessage id="account.invoices.list.error" defaultMessage="Error getting invoices" />
          </Alert>
        )}

        {getInvoicesQuery.isLoading && (
          <div className="pd-flex pd-justify-center pd-p-10">
            <Spinner size="lg" />
          </div>
        )}

        {!getInvoicesQuery.isLoading && invoices.length === 0 && (
          <Alert variant="dim">
            <div className="pd-flex pd-justify-center">
              <FormattedMessage
                id="account.orders.noInvoices"
                defaultMessage="Aww, sadly we cannot find any invoices for the specified date! :("
              />
            </div>
          </Alert>
        )}

        {invoices.map((invoice) => (
          <Panel className="pd-p-0">
            <div className="pd-p-6">
              <div className="pd-mb-2 pd-flex pd-items-center pd-gap-2">
                <Heading as="h3">
                  <FormattedMessage
                    id="account.invoices.invoiceNumber"
                    defaultMessage="Invoice {invoiceNumber}"
                    values={{ invoiceNumber: invoice.invoiceNumber }}
                  />
                </Heading>
                {invoice.changeAscriptionStatus === 'credited' && (
                  <Badge variant="info">
                    <FormattedMessage id="account.invoices.list.change.ascription.status.credited" defaultMessage="Invoice Credited" />
                  </Badge>
                )}
                {invoice.changeAscriptionStatus === 'pending' && (
                  <Badge variant="warning">
                    <FormattedMessage id="account.invoices.list.change.ascription.status.pending" defaultMessage="Change Ascription Pending" />
                  </Badge>
                )}
              </div>
              <DescriptionList className="pd-gap-x-6 pd-gap-y-1 pd-text-sm">
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.invoices.invoiceNumber" defaultMessage="Invoice number" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>{invoice.invoiceNumber}</DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.invoices.invoiceDate" defaultMessage="Invoice date" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>
                    <FormattedDate value={invoice.invoiceDate} />
                  </DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.invoices.totalAmount" defaultMessage="Total amount" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>
                    <FormattedNumber value={Number(invoice.totalAmount)} currency="EUR" style="currency" />
                  </DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.invoices.paymentStatus" defaultMessage="Payment status" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>
                    {invoice.paymentStatus === 'Paid' && (
                      <Badge variant="success">
                        <FormattedMessage id="account.invoices.paymentStatus.paid" defaultMessage="Paid" />
                      </Badge>
                    )}
                    {invoice.paymentStatus === 'Not paid' && (
                      <Badge variant="error">
                        <FormattedMessage id="account.invoices.paymentStatus.unpaid" defaultMessage="Unpaid" />
                      </Badge>
                    )}
                    {invoice.paymentStatus === 'Partpaid' && (
                      <Badge variant="primary">
                        <FormattedMessage id="account.invoices.paymentStatus.partiallyPaid" defaultMessage="Partially paid" />
                      </Badge>
                    )}
                  </DescriptionList.Content>
                </>
                <>
                  <DescriptionList.Title>
                    <FormattedMessage id="account.invoices.orderNumber" defaultMessage="Order number" />
                  </DescriptionList.Title>
                  <DescriptionList.Content>{invoice.orderNumber}</DescriptionList.Content>
                </>

                {invoice.orderReference && (
                  <>
                    <DescriptionList.Title>
                      <FormattedMessage id="account.invoices.orderReference" defaultMessage="Order reference" />
                    </DescriptionList.Title>
                    <DescriptionList.Content>{invoice.orderReference}</DescriptionList.Content>
                  </>
                )}
              </DescriptionList>
            </div>
            <div className="pd-flex pd-items-start pd-justify-end pd-gap-4 pd-rounded-b-md pd-border-t-1 pd-bg-dim-100 pd-px-6 pd-py-4">
              {needPayment(invoice) && (
                <InvoicePaymentButton invoiceNumber={invoice.invoiceNumber} />
              )}
              <FeatureToggle id="mmm_change_ascription">
                <FeatureToggle.Enabled>
                  <InvoiceChangeAscriptionButton
                    invoiceDate={invoice.invoiceDate}
                    invoiceNumber={invoice.invoiceNumber}
                    changeAscriptionStatus={invoice.changeAscriptionStatus}
                  />
                </FeatureToggle.Enabled>
              </FeatureToggle>
              <InvoiceDownloadButton invoiceNumber={invoice.invoiceNumber} />
            </div>
          </Panel>
        ))}

        <div>
          <LinkAsButton
            variant="secondary"
            iconBefore="chevron-left"
            onClick={() => navigate('..')}
            className="pd-text-sm"
          >
            <FormattedMessage
              id="account.linkToAccount"
              defaultMessage="Link to account"
            />
          </LinkAsButton>
        </div>
      </div>
    </>
  );
};

export default InvoicesPage;
